import React from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';

const containerStyle = { width: '100%', height: '100vh' };
const FALLBACK_CENTER = { lat: -13.9626, lng: 33.7741 }; // Lilongwe

const MAP_OPTS = {
  disableDefaultUI: true,
  zoomControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  mapTypeControl: false,
  clickableIcons: false,
  styles: [
    { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  ],
};

/**
 * Driver map background.
 * - Heatmap-ish dots represent demand zones (size+opacity, not just color).
 * - Driver's own location marker (subtle) when provided.
 *
 * `demandPoints`: array of { lat, lng, weight } where weight in [1..3].
 *   We render a Marker with a scaled SVG circle for each.
 */
const DriverHomeMap = ({ driverLocation, demandPoints = [] }) => {
  const center = driverLocation || FALLBACK_CENTER;

  // Build SVG marker icons keyed by weight to avoid recreating per-render.
  const dotIcon = (weight) => {
    const scale = 16 + Math.min(2, Math.max(0, weight - 1)) * 10; // 16 / 26 / 36
    const opacity = 0.18 + Math.min(2, Math.max(0, weight - 1)) * 0.08; // 0.18 / 0.26 / 0.34
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${scale}" height="${scale}" viewBox="0 0 ${scale} ${scale}">
      <circle cx="${scale / 2}" cy="${scale / 2}" r="${scale / 2}" fill="%2316A34A" fill-opacity="${opacity}" />
      <circle cx="${scale / 2}" cy="${scale / 2}" r="${scale / 4}" fill="%2316A34A" fill-opacity="${Math.min(1, opacity + 0.18)}" />
    </svg>`;
    if (typeof window !== 'undefined' && window.google) {
      return {
        url: `data:image/svg+xml;charset=UTF-8,${svg}`,
        scaledSize: new window.google.maps.Size(scale, scale),
        anchor: new window.google.maps.Point(scale / 2, scale / 2),
      };
    }
    return undefined;
  };

  const driverIcon = (() => {
    if (typeof window === 'undefined' || !window.google) return undefined;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
      <circle cx="18" cy="18" r="16" fill="%2316A34A" fill-opacity="0.15" />
      <circle cx="18" cy="18" r="8" fill="%2316A34A" stroke="white" stroke-width="3" />
    </svg>`;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${svg}`,
      scaledSize: new window.google.maps.Size(36, 36),
      anchor: new window.google.maps.Point(18, 18),
    };
  })();

  return (
    <div className="absolute inset-0 z-0" data-testid="driver-home-map">
      <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={13} options={MAP_OPTS}>
        {demandPoints.map((p, i) => (
          <Marker
            key={`dp-${i}-${p.lat}-${p.lng}`}
            position={{ lat: p.lat, lng: p.lng }}
            icon={dotIcon(p.weight || 1)}
            clickable={false}
          />
        ))}
        {driverLocation && (
          <Marker position={driverLocation} icon={driverIcon} clickable={false} />
        )}
      </GoogleMap>
    </div>
  );
};

export default DriverHomeMap;
